import type { Tile } from '../game/types.js';

/**
 * Spell out a single pip value for screen-reader narration. `0` reads as
 * "blank" (the conventional domino term — "the five-blank", "double-blank"),
 * everything else as a digit string. Used in both `tileName` and locale
 * strings via the `pip.<n>` keys when callers need the standalone word.
 */
export function pipName(pip: number, blankLabel: string): string {
  return pip === 0 ? blankLabel : String(pip);
}

/**
 * Spell out a tile for narration:
 *   non-double — "the 5-3" (lower-pip first, en-dash)
 *   double     — "the double-five" / "the double-blank"
 *
 * Both EN and RU share the digit-digit shape; the variant difference is
 * which word the locale uses for "the", "double", and "blank". Caller
 * passes a small translator so the strings stay localised. The numeric
 * order is canonical (`tile[0]` is always the lower pip — see `makeTile`).
 */
export function tileName(
  tile: Tile,
  parts: {
    readonly the: string;
    readonly doublePrefix: string;
    readonly blankLabel: string;
  },
): string {
  if (tile[0] === tile[1]) {
    return `${parts.the} ${parts.doublePrefix}${pipName(tile[0], parts.blankLabel)}`;
  }
  return `${parts.the} ${pipName(tile[0], parts.blankLabel)}-${pipName(tile[1], parts.blankLabel)}`;
}
